import { isBrowserMonitoringEnabled, setCustomAttribute, setUser as setNewRelicUser } from '@zealot/monitoring/browser';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { clearFeatureFlagRegistry } from '../hooks/useFeatureFlag';

type PostHogIdentifyProps = {
  user?: {
    email?: string;
    name?: string;
  } | null;
  isAuthenticated: boolean;
  isPending?: boolean;
  userType?: 'brand' | 'fan';
  brandUuid?: string;
  brandUserUuid?: string;
};

// gloss
const ANONYMOUS = 'anonymous';

// gloss
const identityKey = (email: string, brandUuid?: string, brandUserUuid?: string) =>
  [email, brandUuid ?? '', brandUserUuid ?? ''].join('|');

// gloss
export const PostHogIdentify = ({
  user,
  isAuthenticated,
  isPending = false,
  userType = 'fan',
  brandUuid,
  brandUserUuid,
}: PostHogIdentifyProps) => {
  const posthog = usePostHog();
  const lastIdentifiedUserRef = useRef<string | null>(null);
  const isIdentifyingRef = useRef(false);

  useEffect(() => {
    if (!posthog) return;
    if (isIdentifyingRef.current) return;
    if (isPending) return;

    const environment = process.env.NEXT_PUBLIC_ENVIRONMENT || 'local';
    const brandCode = localStorage.getItem('brandCode') || localStorage.getItem('brand');

    if (!isAuthenticated || !user?.email) {
      if (lastIdentifiedUserRef.current === ANONYMOUS) return;
      if (lastIdentifiedUserRef.current !== null) {
        posthog.reset();
        clearFeatureFlagRegistry();
      }
      posthog.resetPersonPropertiesForFlags();
      lastIdentifiedUserRef.current = ANONYMOUS;
      return;
    }

    const key = identityKey(user.email, brandUuid, brandUserUuid);
    if (lastIdentifiedUserRef.current === key) {
      return;
    }

    isIdentifyingRef.current = true;

    const identifyData = {
      email: user.email,
      name: user.name,
      userType,
      ...(brandCode && { brandCode }),
      ...(brandUuid && { brandUuid }),
      ...(brandUserUuid && { brandUserUuid }),
    };

    posthog.resetPersonPropertiesForFlags(false);
    posthog.setPersonPropertiesForFlags(identifyData, false);

    posthog.identify(user.email, identifyData);

    if (isBrowserMonitoringEnabled()) {
      setNewRelicUser({ id: user.email, email: user.email, type: userType });
      if (brandCode) {
        setCustomAttribute('brandCode', brandCode);
      }
      if (brandUuid) {
        setCustomAttribute('brandUuid', brandUuid);
      }
      setCustomAttribute('userType', userType);
    }

    posthog.group('environment', environment);

    lastIdentifiedUserRef.current = key;
    isIdentifyingRef.current = false;

    posthog.reloadFeatureFlags();
  }, [posthog, isAuthenticated, isPending, user?.email, user?.name, userType, brandUuid, brandUserUuid]);

  return null;
};
